/**
 * Chrome del motor A0 — el header que lo ata visualmente a speak
 * (docs/speak-plan/32-a0-design-brief.md §5.A, pieza A).
 *
 * Reemplaza el badge estático "A0" (que se leía "AO") por un **chip de nivel con
 * anillo de progreso**: el anillo se llena con el medidor de graduación (`gradMeter`,
 * % del corpus afianzado) y el "0" lleva un punto interior para distinguirlo de la
 * "O". A la derecha, el **wallet de créditos** reusado tal cual de la Home
 * (`WalletButton` + `useCredits`). El TabBar (nav) lo monta `A0Screen` al pie. Sólo
 * en el home (no durante la lección, que es un flujo enfocado).
 */
import { WalletButton } from '../../billing/WalletButton.js';
import type { CreditsBalance } from '../../lib/billingApi.js';

/** Radio del anillo (= bundle: 48px de caja, sw 3.5). */
const RING_R = 22.25;
const RING_C = 2 * Math.PI * RING_R;

interface A0AppBarProps {
  /** Medidor de graduación (ítems afianzados / total) → llena el anillo. */
  gradMeter: { have: number; need: number };
  balance: CreditsBalance | null;
  balanceLoading: boolean;
  balanceError: Error | null;
  onRetryBalance: () => void;
  /**
   * DEV-only: toggle del panel de debug A0. Si está presente (sólo en dev, lo pasa
   * `A0Screen`), se monta un botón ⚙ **a la izquierda del wallet** — así el debug vive
   * DENTRO del header en vez de un pill flotante que pisa el chip de nivel.
   */
  onToggleDebug?: () => void;
}

export function A0AppBar({
  gradMeter,
  balance,
  balanceLoading,
  balanceError,
  onRetryBalance,
  onToggleDebug,
}: A0AppBarProps) {
  const pct = gradMeter.need > 0 ? Math.min(1, Math.max(0, gradMeter.have / gradMeter.need)) : 0;

  return (
    <div className="a0-appbar">
      <div className="a0-lvl">
        <span className="a0-ring">
          <svg width={48} height={48} viewBox="0 0 48 48" className="a0-ring-svg" aria-hidden="true">
            <circle cx={24} cy={24} r={RING_R} fill="none" stroke="rgba(255,255,255,0.10)" strokeWidth={3.5} />
            <circle
              cx={24}
              cy={24}
              r={RING_R}
              fill="none"
              stroke="var(--lime)"
              strokeWidth={3.5}
              strokeLinecap="round"
              strokeDasharray={RING_C}
              strokeDashoffset={RING_C * (1 - pct)}
              className="a0-ring-fill"
            />
          </svg>
          <span className="a0-lvl-badge">
            <span className="a0-lvl-a">A</span>
            <span className="a0-lvl-zero">0</span>
          </span>
        </span>
        <div className="a0-lvl-txt">
          <div className="a0-lvl-kick">Tu nivel</div>
          <div className="a0-lvl-name">Primeros pasos</div>
        </div>
      </div>
      <div className="a0-appbar-right">
        {onToggleDebug && (
          <button
            type="button"
            className="a0-dbg-toggle"
            onClick={onToggleDebug}
            title="Panel de debug A0 (dev)"
            aria-label="Panel de debug A0"
          >
            ⚙
          </button>
        )}
        <WalletButton
          balance={balance}
          loading={balanceLoading}
          error={balanceError}
          onRetry={onRetryBalance}
          size="compact"
        />
      </div>
    </div>
  );
}
